import React, { ReactElement, useEffect, useRef, useState } from 'react'
import ms from 'ms'
import Button from '../../button'
import Icon from '../../icon'
import classnames from 'classnames/bind'
import * as blueprint from '@adc/blueprint'
import styles from './styles.scss'
const cx = classnames.bind(styles)

type FilterPanelProps = {
  children: ReactElement<ChildProps> | ReactElement<ChildProps>[]
  elmId?: string
  isDrawer?: boolean
  isEnhancedDrawer?: boolean
  isLoading?: boolean
  onOpen: (isOpen: boolean) => void
  onReset: () => void
  resultsAmount?: number
  showCloseButton?: boolean
}

type ChildProps = {
  elmId: string
}

const FilterPanel: React.FunctionComponent<FilterPanelProps> = ({
  children, // filter group layout generally
  elmId = 'filter_panel',
  isDrawer = false,
  isEnhancedDrawer = false,
  isLoading = false,
  onOpen,
  onReset,
  resultsAmount,
  showCloseButton = false,
}) => {
  // pass elmId thru to children
  const content = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        elmId: `${elmId}_content`,
        ...(isEnhancedDrawer && { noPadding: true }),
      })
    }
    return child
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)

  // overflow detection
  useEffect(() => {
    const container = containerRef.current
    const checkOverflow = () => {
      if (container) {
        const isOverflowing = container.scrollHeight > container.clientHeight
        setHasOverflow(isOverflowing)
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    return () => window.removeEventListener('resize', checkOverflow)
  }, [content])

  // intersection observer to detect if at bottom
  useEffect(() => {
    const container = containerRef.current
    const sentinel = sentinelRef.current

    if (!container || !sentinel || !hasOverflow) {
      setAtBottom(!hasOverflow) // If no overflow, we're always "at bottom"
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setAtBottom(entry.isIntersecting)
      },
      {
        root: container,
        rootMargin: '0px 0px -1px 0px', // Slight margin to trigger before fully visible
        threshold: 0, // Trigger as soon as any part is visible
      }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasOverflow]) // Re-run when overflow state changes

  // Alternative: use scroll event as fallback
  useEffect(() => {
    const container = containerRef.current

    if (!container || !hasOverflow) {
      return undefined
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const top = Number(scrollTop) || 0
      const height = Number(clientHeight) || 0
      const totalHeight = Number(scrollHeight) || 0
      const isAtBottom = top + height >= totalHeight - 5 // 5px tolerance
      setAtBottom(isAtBottom)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [hasOverflow, content])

  const onOpenHandler = (show: boolean) => {
    // tacking on a slight delay to allow for blur events to process first
    setTimeout(() => {
      onOpen(show)
    }, ms('100ms'))
  }

  return (
    <div
      className={cx('panel', {
        'no-overflow': !hasOverflow,
        'at-bottom': atBottom,
        'drawer-panel': isDrawer,
        'enhanced-drawer-panel': isEnhancedDrawer,
      })}
    >
      {showCloseButton && (
        <Icon
          data-elm-id={`${elmId}_close_button`}
          className={cx('close-button')}
          name='times'
          iconStyle='regular'
          fitted
          size='large'
          onClick={() => onOpenHandler(false)}
        />
      )}
      <div
        ref={containerRef}
        className={cx('content', {
          'drawer-content': isDrawer,
          'enhanced-drawer-content': isEnhancedDrawer,
        })}
      >
        <div>{content}</div>
        <div ref={sentinelRef} className={cx('sentinel')} />
      </div>

      <div
        className={cx({
          'footer-container': isDrawer,
          'enhanced-footer-container': isEnhancedDrawer,
        })}
      >
        {isDrawer && <hr className={cx('divider')} />}
        <div
          className={cx('footer', {
            'drawer-footer': isDrawer,
            'enhanced-drawer-footer': isEnhancedDrawer,
          })}
        >
          <Button
            role='tertiary'
            elmId={`${elmId}_reset_button`}
            className={cx('reset-button')}
            onClick={onReset}
          >
            {isDrawer ? 'Reset all filters' : 'Reset'}
          </Button>
          <Button
            className={cx('done-button')}
            elmId={`${elmId}_done_button`}
            loading={isLoading}
            onClick={() => onOpenHandler(false)}
            role='primary'
          >
            {resultsAmount
              ? `See ${blueprint.filters.utils.formatNumber(
                  resultsAmount.toString()
                )} homes`
              : 'Done'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FilterPanel
